import React, { useEffect, useState } from 'react';

import { makeStyles, Theme } from '@material-ui/core/styles';
import { debounce } from 'lodash';
import * as Raven from 'raven-js';

import {
  Autocomplete,
  BackLink,
  Button,
  Typography,
} from '../../components/atoms';
import { BottomBar, Hidden, Page } from '../../components/layouts';
import { AddressFormat } from '../../components/molecules';
import { DispatchProps, StateProps } from '../../weborder/component/Map';
import * as Data from '../../weborder/Data';
import * as Store from '../../weborder/Store';
import { Utilities } from '../../weborder/Utilities';
import { domUtils } from '../../weborder/utils';

type Props = StateProps & DispatchProps;

const useStyles = makeStyles((theme: Theme) => ({
  autocomplete: {
    '&.MuiAutocomplete-root': {
      border: `1px solid ${theme.palette.grey[400]}`,
      margin: theme.spacing(0, 2, 2),
    },
  },

  geolocationButton: {
    color: theme.palette.grey[800],
    display: 'inline-flex',
    margin: theme.spacing(0, 0, 2.75, 2),
    '& button': {
      marginLeft: theme.spacing(0.625),
    },
  },

  map: {
    boxSizing: 'content-box',
    height: '260px',
    marginBottom: theme.spacing(2),
    width: '100%',
    '&:after': {
      background:
        'url("https://maps.gstatic.com/mapfiles/api-3/images/spotlight-poi_hdpi.png")',
      backgroundSize: '22px 40px',
      content: '""',
      display: 'block',
      height: '40px',
      left: '50%',
      margin: '-18px 0 0 -11px',
      pointerEvents: 'none',
      position: 'absolute',
      top: '50%',
      width: '22px',
    },
  },

  [theme.breakpoints.up('sm')]: {
    autocomplete: {
      '&.MuiAutocomplete-root': {
        margin: theme.spacing(0, 3, 2),
      },
    },

    geolocationButton: {
      margin: theme.spacing(0, 0, 2.75, 3),
    },
  },

  [theme.breakpoints.up('md')]: {
    autocomplete: {
      '&.MuiAutocomplete-root': {
        border: 'none',
        margin: theme.spacing(0, 0, 2),
      },
      '& .MuiAutocomplete-inputRoot[class*="MuiInput-root"] .MuiAutocomplete-input:first-child': {
        padding: theme.spacing(1.5, 1.25),
      },
    },

    geolocationButton: {
      margin: theme.spacing(0, 0, 2.75),
    },

    map: {
      border: `1px solid ${theme.palette.grey[400]}`,
      height: '200px',
      marginBottom: theme.spacing(2.5),
    },
  },
}));

export function DeliveryCustomLocation(props: Props): JSX.Element {
  const {
    AppConfig: appConfig,
    AddLocation: addLocation,
    Config: config,
    Location: location,
    translate,
  } = props;

  const [geolocationInProgress, setGeolocationInProgress] = useState(false);
  const [map, setMap] = useState(null);
  const [places, setPlaces] = useState(null);
  const [suggestions, setSuggestions] = useState([]);

  const classes = useStyles();

  useEffect(() => {
    domUtils.skipToContent();
  }, []);

  useEffect(() => {
    config.MapLoader.then(initialiseMap);
  }, []);

  const findUserLocation = () => {
    if (!Utilities.browserHasLocation()) {
      alert(
        'Your browser does not support geolocation lookup. We cannot use your location - please enter your address instead.',
      );
      Raven.captureMessage('Geolocation not supported', {
        tags: { category: 'Third party library failure' },
      });
      return;
    }

    config.MapLoader.then(() => {
      if (map) {
        setGeolocationInProgress(true);

        navigator.geolocation.getCurrentPosition(
          (p) => handleGeolocationSuccess(map, p),
          handleGeolocationError,
          {
            enableHighAccuracy: true,
          },
        );
      }
    });
  };

  const initialiseMap = () => {
    const options: google.maps.MapOptions = {
      center: Data.LatLng(location),
      clickableIcons: false,
      mapTypeControl: false, // No map type (terrain/satelite, etc)
      streetViewControl: false, // No street view ControlLabel
      zoom: 17,
      zoomControlOptions: {
        position: google.maps.ControlPosition.RIGHT_CENTER,
      },
    };

    const map = new google.maps.Map(
      document.getElementById('mapRef') as Element,
      options,
    );

    const centerChanged = debounce(() => {
      const newCenter = Data.ToGeo(map.getCenter());
      const userCertainity = Store.LocationCertainty.User;
      addLocation(newCenter, userCertainity);
    }, 500);

    centerChanged();

    map.addListener('center_changed', centerChanged);

    setMap(map);
    setPlaces(new google.maps.places.PlacesService(map));
  };

  const handleGeolocationError = (error: GeolocationPositionError) => {
    alert(
      `${translate('Error_location')}.\n\n${translate(
        'Enter_address',
      )}.\n\n${translate('Error')} ${
        error.code === 1
          ? translate('Denied_geolocation')
          : error.code === 2
          ? translate('Permission_geolocation')
          : error.message
      }`,
    );

    setGeolocationInProgress(false);

    Raven.captureMessage('Geolocation error callback', {
      tags: { category: 'Third party library failure' },
      extra: {
        error: Utilities.handleError(error),
      },
    });
  };

  const handleGeolocationSuccess = (
    map: google.maps.Map,
    position: GeolocationPosition,
  ) => {
    const location = new google.maps.LatLng(
      position.coords.latitude,
      position.coords.longitude,
    );
    const center = Data.ToGeo(location);

    map.panTo(location);
    setGeolocationInProgress(false);
    addLocation(center, Store.LocationCertainty.GeoLocation);
  };

  const handlePlaceSelected = (_event, selectedLocation) => {
    if (!selectedLocation || selectedLocation.length === 0 || !map || !places) {
      return;
    }

    const request: any = {
      placeId: selectedLocation.place_id,
      fields: ['geometry', 'address_component'],
    };
    places.getDetails(request, (result, _status) => {
      addLocation(
        Data.ToGeo(result.geometry.location),
        Store.LocationCertainty.User,
      );
      map.panTo(result.geometry.location);
    });
  };

  const navigationEnabled = (): boolean =>
    map !== null && !geolocationInProgress;

  const handleInputChange = debounce((input) => {
    const inputValue = input.target.value;
    const minGeocodeInputLength = 5;
    if (inputValue.length < minGeocodeInputLength) {
      return;
    }
    input = inputValue;

    config.MapLoader.then((tools) => {
      let bounds: google.maps.LatLngBoundsLiteral | undefined;
      if (appConfig.status === 'success') {
        bounds = {
          // North East : bigger
          // South West : smaller
          north: appConfig.data.MapBoundryMaxLatitude,
          south: appConfig.data.MapBoundryMinLatitude,
          east: appConfig.data.MapBoundryMaxLongitude,
          west: appConfig.data.MapBoundryMinLongitude,
        };
      }

      tools.AutoComplete.getPlacePredictions(
        {
          bounds,
          input,
          location: Data.LatLng(location),
          radius: 200, // TODO: tweak 200m?
        },
        (suggestions: google.maps.places.AutocompletePrediction[]) => {
          if (suggestions) {
            setSuggestions(suggestions);
          }
        },
      );
    });
  }, 250);

  const geolocationTitle = geolocationInProgress
    ? translate('Finding_location')
    : translate('Use_your_current_location');

  const handleConfirmAddress = () => {
    Utilities.navigateTo(
      `/delivery/custom/${location.Latitude}/${location.Longitude}`,
    );
  };

  return (
    <>
      <Page pageName="delivery-custom-location">
        <header>
          <BackLink label={translate('Back')} linkPath="/delivery/new" />

          <Typography tabIndex={0} variant="h1">
            {translate('Type_your_address')}
          </Typography>
        </header>

        <article>
          <Autocomplete
            className={classes.autocomplete}
            filterOptions={(options) => options}
            getOptionLabel={(option) =>
              typeof option === 'string' ? option : option.description
            }
            onChange={handlePlaceSelected}
            onInputChange={handleInputChange}
            options={suggestions}
            placeholder={translate('Enter_your_address_here')}
            renderOption={(option) => <AddressFormat option={option} />}
          />

          {Utilities.browserHasLocation() && (
            <div className={classes.geolocationButton}>
              <Typography>{translate('Or')} </Typography>
              <Button
                data-fd="geolocation-button"
                disabled={!navigationEnabled()}
                key="geolocation-button"
                onClick={findUserLocation}
                variant="text"
              >
                {geolocationTitle}
              </Button>
            </div>
          )}

          <div
            aria-hidden="true"
            className={classes.map}
            data-fd="custom-location-map"
            id="mapRef"
          />

          <Hidden smDown>
            <Button fullWidth onClick={handleConfirmAddress}>
              {translate('Confirm_address')}
            </Button>
          </Hidden>
        </article>

        <Hidden mdUp>
          <BottomBar fixedOnly={['xs', 'sm']}>
            <Button fullWidth onClick={handleConfirmAddress}>
              {translate('Confirm_address')}
            </Button>
          </BottomBar>
        </Hidden>
      </Page>
    </>
  );
}
